import moment from 'moment';

import { NON_SESSION_BUFFER_START_TIME, SESSION_BUFFER_START_TIME } from './constants';
import { AgendaItem, AgendaTrack } from './types';

import {
  EVENT_MEDIALIVE_CHANNEL_START_TIME,
  SIMULIVE_TESTING_BUFFER_TIME,
  SIMULIVE_TESTING_BUFFER_TO_START_EVENT_CHANNELS
} from '@/constants.js';
import store from '@/store';
import { getCleanTimezoneAbbrev } from '@/utils/timezone';

export function getTotalVideoDurationForSession(broadcast) {
  let totalVideoDuration = 0;
  broadcast?.video_assets?.forEach(video => {
    totalVideoDuration += video.duration;
  });

  return totalVideoDuration * 1000;
}

export function canShowSimuliveTestUI(
  currentTime,
  eventStartTime,
  totalVideoDuration,
  broadcastType,
  isChannelRunning
) {
  if (broadcastType !== 'SIMULIVE') {
    return false;
  }

  const channelStartBuffer = EVENT_MEDIALIVE_CHANNEL_START_TIME + SIMULIVE_TESTING_BUFFER_TIME + totalVideoDuration;

  const testingEndBuffer = EVENT_MEDIALIVE_CHANNEL_START_TIME + SIMULIVE_TESTING_BUFFER_TO_START_EVENT_CHANNELS;

  const maxSimuliveTestingStartTime = moment(eventStartTime, 'YYYY-MM-DD HH:mm:ssZ').subtract(
    channelStartBuffer,
    'millisecond'
  );

  const timeWhenChannelStartsForEvent = moment(eventStartTime, 'YYYY-MM-DD HH:mm:ssZ').subtract(
    testingEndBuffer,
    'millisecond'
  );

  return (
    currentTime.isBefore(maxSimuliveTestingStartTime) ||
    (isChannelRunning && currentTime.isBefore(timeWhenChannelStartsForEvent))
  );
}

export function getSimuliveStateMessage(broadcastType, channelState, isAnyAssetProcessing) {
  if (broadcastType != 'SIMULIVE') {
    return '';
  }

  if (isAnyAssetProcessing) {
    return 'Processing...';
  }

  const simuliveStateMap = {
    RUNNING: 'Ongoing',
    STARTING: 'Starting test...',
    STOPPING: 'Stopping Test',
    PROCESSING: 'Processing',
    default: 'Not started'
  };

  return simuliveStateMap[channelState] || simuliveStateMap.default;
}

export function getSimuliveTestingButtonMessage(channelState) {
  const buttonMessageMap = {
    RUNNING: 'Stop Test',
    IDLE: 'Start Test',
    STOPPING: 'Stopping...',
    STARTING: 'Starting...',
    default: 'Setting Up'
  };

  return buttonMessageMap[channelState] || buttonMessageMap.default;
}

export function checkIfSimuliveTestingBtnDisabled(channelState) {
  return channelState !== 'IDLE' && channelState !== 'RUNNING';
}

export const formatToSimuliveData = broadcast => {
  if (!broadcast) {
    return;
  }

  const { currentDateTime } = store.state.clientClock;
  const currentEvent = store.getters['studioEventList/getCurrentEvent'];
  const broadcastType = broadcast.broadcast_type;
  const isBroadcastSimulive = broadcastType === 'SIMULIVE';
  const totalVideoItemDuration = getTotalVideoDurationForSession(broadcast);

  const videoAddedToBroadcast = totalVideoItemDuration > 0;
  const channelState =
    broadcast.test_simulive_processing_state || (broadcast.simulive_test_run_end_time ? 'RUNNING' : 'IDLE');

  const allowSimuliveTesting = broadcast
    ? canShowSimuliveTestUI(
        currentDateTime,
        currentEvent?.start_time,
        totalVideoItemDuration,
        broadcastType,
        channelState
      )
    : false;
  const isAnyAssetProcessing = broadcast.video_assets.some(asset => asset.transcode_status !== 'COMPLETE');
  const simuliveStateMessage = getSimuliveStateMessage(broadcastType, channelState, isAnyAssetProcessing);

  const simuliveTestingBtnMessage = getSimuliveTestingButtonMessage(channelState);
  const isSimuliveTestingBtnDisabled = isAnyAssetProcessing || checkIfSimuliveTestingBtnDisabled(channelState);

  const simuliveTestingEndTimeMsg =
    broadcast.simulive_test_run_end_time && allowSimuliveTesting
      ? 'The videos start playing in session after 5 minutes of starting the test run. Testing will end once all videos have played.'
      : '';

  return {
    ...broadcast,
    channelState,
    simuliveStateMessage,
    allowSimuliveTesting,
    simuliveTestingBtnMessage,
    isSimuliveTestingBtnDisabled,
    simuliveTestingEndTimeMsg,
    isShowingSimuliveTestingUI: broadcast && videoAddedToBroadcast && isBroadcastSimulive,
    videoAddedToBroadcast,
    totalVideoItemDuration
  };
};

export const updateAgendaItemInTracks = (agendaItem: AgendaItem, tracks: AgendaTrack[]) => {
  return tracks.map(track => {
    if (agendaItem.track?.includes(track.id || '') && !track.agenda_items.includes(agendaItem.id.toString())) {
      const newTrack = {
        ...track,
        agenda_items: track.agenda_items ? `${track.agenda_items},${agendaItem.id}` : `${agendaItem.id}`
      };
      newTrack.agenda_items = newTrack.agenda_items.replace(/^,+/g, '');
      return newTrack;
    }
    if (!agendaItem.track?.includes(track.id || '')) {
      const newTrack = {
        ...track,
        agenda_items: track.agenda_items ? track.agenda_items.replace(`${agendaItem.id}`, '') : ''
      };
      newTrack.agenda_items = newTrack.agenda_items.replace(/^,+/g, '');
      return newTrack;
    }
    track.agenda_items.replace(/^,+/g, '');
    return track;
  });
};

export function getItemsTimeRange(startDate: string, endDate: string, timezone: string): string {
  if (!timezone) {
    return '';
  }

  const timezoneIdentifier = timezone.split(' ')[0];
  const startTz = moment.tz(startDate, timezoneIdentifier);
  const endTz = moment.tz(endDate, timezoneIdentifier);

  const timezoneAbbrev = getCleanTimezoneAbbrev(startTz.format('z'), timezone);

  if (startTz.isDST() !== endTz.isDST()) {
    return `${startTz.format('h:mm A')} ${timezoneAbbrev} - ${endTz.format('h:mm A')} ${timezoneAbbrev}`;
  }

  return `${startTz.format('h:mm A')} - ${endTz.format('h:mm A')} ${timezoneAbbrev}`;
}

export function getFormattedDate(startDate: string, timezone: string, format: string = 'D MMM YYYY hh:mm a'): string {
  if (!timezone) {
    return '';
  }

  const formatedTimezone = timezone.split(' ')[0];
  const startDay = moment.tz(startDate, formatedTimezone).format(format);
  return startDay;
}

export function getLocaleDateString(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

export function isDateInPast(date: string): boolean {
  const currentDate = new Date();
  const selectedDate = new Date(date);
  selectedDate.setHours(23);
  selectedDate.setMinutes(59);
  selectedDate.setSeconds(59);
  return currentDate > selectedDate;
}

export function isEventAboutToStart(startTime: string): boolean {
  return new Date().getTime() + 3600000 > new Date(startTime).getTime();
}

export function dateTo12HFormat({ date, showDate = true }: { date: string | Date; showDate?: boolean }): string {
  const dateTimeWithoutOffset = moment.parseZone(date).format('YYYY-MM-DDTHH:mm:ss');

  if (!showDate) {
    return moment(dateTimeWithoutOffset).format('hh:mm a');
  }

  return moment(dateTimeWithoutOffset).format('MMMM Do YYYY hh:mm a');
}

export function removeAgendaItemFromTracks(id: string, tracks: AgendaTrack[]) {
  return tracks.map(track => {
    if (track.agenda_items.includes(id)) {
      const newTrack = {
        ...track,
        agenda_items: track.agenda_items.replace(`${id}`, '')
      };
      newTrack.agenda_items = newTrack.agenda_items.replace(/^,+/g, '');
      return newTrack;
    }
    return track;
  });
}

export const startTime = {
  session(timezoneName: string, sessionBufferStartTime: number = SESSION_BUFFER_START_TIME) {
    return moment.tz(new Date(), timezoneName).add(sessionBufferStartTime, 'minute').format('HH:mm');
  },
  nonSession(timezoneName: string) {
    return moment.tz(new Date(), timezoneName).add(NON_SESSION_BUFFER_START_TIME, 'minute').format('HH:mm');
  }
};

export const endTime = {
  session(timezoneName: string) {
    return moment.tz(new Date(), timezoneName).add({ hours: 1, minutes: SESSION_BUFFER_START_TIME }).format('HH:mm');
  },
  nonSession(timezoneName: string) {
    return moment
      .tz(new Date(), timezoneName)
      .add({ hours: 1, minutes: NON_SESSION_BUFFER_START_TIME })
      .format('HH:mm');
  }
};
